<script setup>
/* eslint-disable no-alert, vue/no-bare-strings-in-template, vue/prefer-separate-static-class */
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TalkviaDeliveryRoutesAPI from 'dashboard/api/talkviaDeliveryRoutes';
import TalkviaGoogleMapsConfigAPI from 'dashboard/api/talkviaGoogleMapsConfig';

const copy = {
  title: 'Rotas',
  subtitle:
    'Planeje e otimize rotas de entrega vinculando contratos e eventos da agenda.',
  actions: {
    newRoute: 'Nova rota',
    googleMapsSettings: 'Google Maps',
    save: 'Salvar',
    cancel: 'Cancelar',
    generate: 'Gerar rota otimizada',
    delete: 'Excluir',
    edit: 'Editar',
    openGoogleMaps: 'Abrir no Google Maps',
    copyGoogleMapsLink: 'Copiar link do mapa',
    openWaze: 'Abrir no Waze',
    addStop: 'Adicionar parada',
    removeStop: 'Remover',
    addFromSource: 'Adicionar da agenda / contratos',
    close: 'Fechar',
    removeKey: 'Remover chave',
    complete: 'Marcar como concluída',
    markDelivered: 'Concluir',
    markNotDelivered: 'Não entregue',
    currentLocation: 'Usar localização atual',
    lookupAddress: 'Buscar endereço',
  },
  fields: {
    name: 'Nome da rota',
    originAddress: 'Endereço de origem',
    stopName: 'Nome / destinatário',
    stopAddress: 'Endereço de entrega',
    stopPhone: 'Telefone',
    stopNotes: 'Observações',
    status: 'Status',
    notes: 'Observações gerais',
    search: 'Buscar rota...',
    googleMapsApiKey: 'Chave API do Google Maps',
  },
  status: {
    draft: 'Rascunho',
    active: 'Ativa',
    completed: 'Concluída',
  },
  sourceType: {
    contract: 'Contrato',
    calendar_event: 'Agenda',
    manual: 'Manual',
  },
  labels: {
    loading: 'Carregando...',
    noRoutes: 'Nenhuma rota cadastrada.',
    noRouteDesc: 'Crie uma nova rota para planejar suas entregas.',
    stops: 'parada(s)',
    origin: 'Origem',
    destination: 'Destino',
    navigationLinks: 'Links de navegação',
    selectSources: 'Selecionar da agenda / contratos',
    contracts: 'Contratos aguardando entrega',
    calendarEvents: 'Eventos da agenda',
    noContracts: 'Nenhum contrato aguardando entrega.',
    noEvents: 'Nenhum evento encontrado.',
    addressRequired: 'Informe o endereço para esta parada',
    googleMapsConfig: 'Configuração do Google Maps',
    googleMapsConfigured: 'Chave configurada',
    googleMapsNotConfigured: 'Sem chave configurada',
    googleMapsHint:
      'Ative a Directions API no Google Cloud e salve a chave desta conta para otimizar usando tempo de percurso.',
    googleMapsTutorialTitle: 'Como gerar a API key',
    googleMapsProductsTitle: 'Produto necessário no Google',
    googleMapsSecurityTitle: 'Restrição recomendada',
  },
  stopStatus: {
    pending: 'Pendente',
    delivered: 'Entregue',
    not_delivered: 'Não entregue',
  },
  messages: {
    created: 'Rota criada com sucesso.',
    updated: 'Rota atualizada.',
    deleted: 'Rota excluída.',
    generated: 'Links de navegação gerados.',
    linkCopied: 'Link do mapa copiado.',
    error: 'Ocorreu um erro. Tente novamente.',
    deleteConfirm: 'Deseja excluir esta rota? Esta ação não pode ser desfeita.',
    noAddress:
      'Informe o endereço de origem e pelo menos uma parada com endereço.',
    addressQueryRequired: 'Digite um CEP ou nome de rua para buscar.',
    addressNotFound: 'Endereço não encontrado.',
    addressLookupFailed: 'Não foi possível buscar o endereço.',
    locationUnsupported: 'Seu navegador não permite obter a localização atual.',
    locationFailed: 'Não foi possível obter sua localização atual.',
    googleMapsSaved: 'Configuração do Google Maps salva.',
    googleMapsRemoved: 'Chave do Google Maps removida.',
  },
};

const routes = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const isGenerating = ref(false);
const isLoadingSources = ref(false);
const isLocatingOrigin = ref(false);
const isSearchingOrigin = ref(false);
const isLoadingGoogleMapsConfig = ref(false);
const isSavingGoogleMapsConfig = ref(false);
const searchQuery = ref('');

const formDialogRef = ref(null);
const sourceDialogRef = ref(null);
const detailDialogRef = ref(null);
const googleMapsConfigDialogRef = ref(null);
const editingRoute = ref(null);
const selectedRoute = ref(null);

const sources = reactive({ contracts: [], calendar_events: [] });
const selectedSourceTab = ref('contracts');

const googleMapsConfig = ref({
  active: true,
  api_key_configured: false,
  masked_api_key: '',
});

const googleMapsForm = reactive({
  api_key: '',
  clear_api_key: false,
});

const googleMapsTutorialSteps = [
  'Acesse o Google Cloud Console e crie ou selecione um projeto.',
  'Vincule uma conta de faturamento ao projeto. O Google exige billing ativo para usar APIs do Maps.',
  'Em APIs e serviços, habilite o produto Directions API (Legacy).',
  'Vá em Credenciais, clique em Criar credenciais e escolha Chave de API.',
  'Copie a chave gerada e cole aqui nesta configuração da conta.',
];

const googleMapsProducts = [
  'Directions API (Legacy): calcula a rota e retorna a ordem otimizada dos pontos.',
];

const googleMapsSecurityTips = [
  'Em Restrição de API, selecione somente Directions API (Legacy).',
  'Em Restrição de aplicativo, prefira IPs de servidor quando a chamada sair do backend.',
];

const form = reactive({
  name: '',
  origin_address: '',
  status: 'draft',
  notes: '',
  stops: [],
});

const filteredRoutes = computed(() => {
  if (!searchQuery.value) return routes.value;
  const q = searchQuery.value.toLowerCase();
  return routes.value.filter(
    r =>
      r.name?.toLowerCase().includes(q) ||
      r.origin_address?.toLowerCase().includes(q)
  );
});

const statusColor = status => {
  const map = {
    draft: 'bg-slate-100 text-slate-600',
    active: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
  };
  return map[status] || 'bg-slate-100 text-slate-600';
};

const sourceTypeColor = type => {
  const map = {
    contract: 'bg-orange-100 text-orange-700',
    calendar_event: 'bg-blue-100 text-blue-700',
    manual: 'bg-slate-100 text-slate-600',
  };
  return map[type] || 'bg-slate-100 text-slate-600';
};

const normalizeStopDeliveryStatus = status => {
  if (['pending', 'delivered', 'not_delivered'].includes(status)) {
    return status;
  }
  return 'pending';
};

const stopStatusColor = status => {
  const map = {
    pending: 'bg-slate-100 text-slate-600 border-slate-200',
    delivered: 'bg-green-100 text-green-700 border-green-200',
    not_delivered: 'bg-rose-100 text-rose-700 border-rose-200',
  };
  return map[normalizeStopDeliveryStatus(status)];
};

const stopCardColor = status => {
  const map = {
    pending:
      'bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700',
    delivered:
      'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800',
    not_delivered:
      'bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800',
  };
  return map[normalizeStopDeliveryStatus(status)];
};

const isStopStatus = (stop, status) =>
  normalizeStopDeliveryStatus(stop?.delivery_status) === status;

const fetchRoutes = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaDeliveryRoutesAPI.get();
    routes.value = (data.routes || []).map(route => ({
      ...route,
      stops: (route.stops || []).map(stop => ({
        ...stop,
        delivery_status: normalizeStopDeliveryStatus(stop.delivery_status),
      })),
    }));
  } catch {
    useAlert(copy.messages.error);
  } finally {
    isLoading.value = false;
  }
};

const fetchSources = async () => {
  isLoadingSources.value = true;
  try {
    const { data } = await TalkviaDeliveryRoutesAPI.getSources();
    sources.contracts = data.contracts || [];
    sources.calendar_events = data.calendar_events || [];
  } catch {
    useAlert(copy.messages.error);
  } finally {
    isLoadingSources.value = false;
  }
};

const applyGoogleMapsConfig = data => {
  googleMapsConfig.value = data.setting || googleMapsConfig.value;
};

const fetchGoogleMapsConfig = async () => {
  isLoadingGoogleMapsConfig.value = true;
  try {
    const { data } = await TalkviaGoogleMapsConfigAPI.get();
    applyGoogleMapsConfig(data);
  } catch {
    useAlert(copy.messages.error);
  } finally {
    isLoadingGoogleMapsConfig.value = false;
  }
};

const openGoogleMapsConfig = async () => {
  googleMapsForm.api_key = '';
  googleMapsForm.clear_api_key = false;
  googleMapsConfigDialogRef.value?.open();
  await fetchGoogleMapsConfig();
};

const saveGoogleMapsConfig = async () => {
  isSavingGoogleMapsConfig.value = true;
  try {
    const payload = {
      active: true,
      clear_api_key: googleMapsForm.clear_api_key,
    };
    if (googleMapsForm.api_key.trim()) {
      payload.api_key = googleMapsForm.api_key.trim();
      payload.clear_api_key = false;
    }
    const { data } = await TalkviaGoogleMapsConfigAPI.update(payload);
    applyGoogleMapsConfig(data);
    googleMapsForm.api_key = '';
    googleMapsForm.clear_api_key = false;
    googleMapsConfigDialogRef.value?.close();
    useAlert(
      payload.clear_api_key
        ? copy.messages.googleMapsRemoved
        : copy.messages.googleMapsSaved
    );
  } catch {
    useAlert(copy.messages.error);
  } finally {
    isSavingGoogleMapsConfig.value = false;
  }
};

const openNewRoute = () => {
  editingRoute.value = null;
  form.name = '';
  form.origin_address = '';
  form.status = 'draft';
  form.notes = '';
  form.stops = [];
  formDialogRef.value?.open();
};

const openEditRoute = route => {
  editingRoute.value = route;
  form.name = route.name;
  form.origin_address = route.origin_address;
  form.status = route.status;
  form.notes = route.notes || '';
  form.stops = (route.stops || []).map(s => ({
    ...s,
    delivery_status: normalizeStopDeliveryStatus(s.delivery_status),
  }));
  formDialogRef.value?.open();
};

const openDetailRoute = route => {
  selectedRoute.value = route;
  detailDialogRef.value?.open();
};

const addManualStop = () => {
  form.stops.push({
    id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
    order: form.stops.length + 1,
    name: '',
    address: '',
    phone: '',
    notes: '',
    source_type: 'manual',
    source_id: null,
    delivery_status: 'pending',
  });
};

const removeStop = index => {
  form.stops.splice(index, 1);
  form.stops.forEach((s, i) => {
    s.order = i + 1;
  });
};

const moveStop = (index, direction) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= form.stops.length) return;
  const temp = form.stops[index];
  form.stops[index] = form.stops[newIndex];
  form.stops[newIndex] = temp;
  form.stops.forEach((s, i) => {
    s.order = i + 1;
  });
};

const openSourceDialog = async () => {
  sourceDialogRef.value?.open();
  if (!sources.contracts.length && !sources.calendar_events.length) {
    await fetchSources();
  }
};

const addSourceToStops = source => {
  const alreadyAdded = form.stops.some(
    s =>
      s.source_type === source.type && String(s.source_id) === String(source.id)
  );
  if (alreadyAdded) return;
  form.stops.push({
    id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
    order: form.stops.length + 1,
    name: source.name || '',
    address: source.address || '',
    phone: source.phone || '',
    notes: source.notes || '',
    source_type: source.type,
    source_id: source.id,
    delivery_status: 'pending',
  });
};

const isSourceAdded = source =>
  form.stops.some(
    s =>
      s.source_type === source.type && String(s.source_id) === String(source.id)
  );

const formatViaCepAddress = data =>
  [
    data.logradouro,
    data.bairro,
    [data.localidade, data.uf].filter(Boolean).join(' - '),
    data.cep,
  ]
    .filter(Boolean)
    .join(', ');

const reverseGeocodeOrigin = async ({ latitude, longitude }) => {
  const fallback = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  const url = new URL('https://nominatim.openstreetmap.org/reverse');
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('lat', latitude);
  url.searchParams.set('lon', longitude);
  url.searchParams.set('zoom', '18');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('accept-language', 'pt-BR');

  try {
    const response = await fetch(url.toString());
    if (!response.ok) return fallback;
    const data = await response.json();
    return data.display_name || fallback;
  } catch {
    return fallback;
  }
};

const useCurrentOriginLocation = async () => {
  if (!navigator.geolocation) {
    useAlert(copy.messages.locationUnsupported);
    return;
  }

  isLocatingOrigin.value = true;
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      });
    });

    form.origin_address = await reverseGeocodeOrigin(position.coords);
  } catch {
    useAlert(copy.messages.locationFailed);
  } finally {
    isLocatingOrigin.value = false;
  }
};

const searchOriginAddress = async () => {
  const query = form.origin_address.trim();
  if (!query) {
    useAlert(copy.messages.addressQueryRequired);
    return;
  }

  isSearchingOrigin.value = true;
  try {
    const cep = query.replace(/\D/g, '');
    if (/^\d{5}-?\d{3}$/.test(query) || cep.length === 8) {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      if (!response.ok) throw new Error('CEP lookup failed');
      const data = await response.json();
      if (data.erro) {
        useAlert(copy.messages.addressNotFound);
        return;
      }
      form.origin_address = formatViaCepAddress(data);
      return;
    }

    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('countrycodes', 'br');
    url.searchParams.set('limit', '1');
    url.searchParams.set('accept-language', 'pt-BR');
    url.searchParams.set('q', query);

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error('Address lookup failed');
    const results = await response.json();
    if (!results.length) {
      useAlert(copy.messages.addressNotFound);
      return;
    }
    form.origin_address = results[0].display_name;
  } catch {
    useAlert(copy.messages.addressLookupFailed);
  } finally {
    isSearchingOrigin.value = false;
  }
};

const saveRoute = async () => {
  if (!form.name.trim() || !form.origin_address.trim()) {
    useAlert('Informe o nome e o endereço de origem.');
    return;
  }
  isSaving.value = true;
  try {
    const payload = {
      name: form.name.trim(),
      origin_address: form.origin_address.trim(),
      status: form.status,
      notes: form.notes,
      stops: form.stops.map(stop => ({
        ...stop,
        delivery_status: normalizeStopDeliveryStatus(stop.delivery_status),
      })),
    };
    if (editingRoute.value) {
      const { data } = await TalkviaDeliveryRoutesAPI.update(
        editingRoute.value.id,
        payload
      );
      const idx = routes.value.findIndex(r => r.id === editingRoute.value.id);
      if (idx !== -1) routes.value[idx] = data;
      useAlert(copy.messages.updated);
    } else {
      const { data } = await TalkviaDeliveryRoutesAPI.create(payload);
      routes.value.unshift(data);
      useAlert(copy.messages.created);
    }
    formDialogRef.value?.close();
  } catch {
    useAlert(copy.messages.error);
  } finally {
    isSaving.value = false;
  }
};

const generateRoute = async route => {
  const stops = route.stops || [];
  const hasAddresses = stops.some(s => s.address?.trim());
  if (!route.origin_address?.trim() || !hasAddresses) {
    useAlert(copy.messages.noAddress);
    return;
  }
  isGenerating.value = true;
  try {
    const { data } = await TalkviaDeliveryRoutesAPI.generate(route.id);
    const idx = routes.value.findIndex(r => r.id === route.id);
    if (idx !== -1) routes.value[idx] = data;
    selectedRoute.value = data;
    detailDialogRef.value?.open();
    useAlert(copy.messages.generated);
  } catch {
    useAlert(copy.messages.error);
  } finally {
    isGenerating.value = false;
  }
};

const copyMapLink = async route => {
  if (!route?.google_maps_url) return;

  try {
    await navigator.clipboard.writeText(route.google_maps_url);
    useAlert(copy.messages.linkCopied);
  } catch {
    window.prompt('Copie o link do mapa:', route.google_maps_url);
  }
};

const deleteRoute = async route => {
  if (!window.confirm(copy.messages.deleteConfirm)) return;
  try {
    await TalkviaDeliveryRoutesAPI.delete(route.id);
    routes.value = routes.value.filter(r => r.id !== route.id);
    if (selectedRoute.value?.id === route.id) {
      detailDialogRef.value?.close();
    }
    useAlert(copy.messages.deleted);
  } catch {
    useAlert(copy.messages.error);
  }
};

const completeRoute = async route => {
  try {
    const { data } = await TalkviaDeliveryRoutesAPI.update(route.id, {
      status: 'completed',
    });
    const idx = routes.value.findIndex(r => r.id === route.id);
    if (idx !== -1) routes.value[idx] = data;
    if (selectedRoute.value?.id === route.id) selectedRoute.value = data;
    useAlert(copy.messages.updated);
  } catch {
    useAlert(copy.messages.error);
  }
};

const updateStopDeliveryStatus = async (route, stopIndex, deliveryStatus) => {
  const stops = (route.stops || []).map((stop, index) => {
    if (index !== stopIndex) {
      return {
        ...stop,
        delivery_status: normalizeStopDeliveryStatus(stop.delivery_status),
      };
    }

    return {
      ...stop,
      delivery_status: normalizeStopDeliveryStatus(deliveryStatus),
    };
  });

  try {
    const { data } = await TalkviaDeliveryRoutesAPI.update(route.id, { stops });
    const idx = routes.value.findIndex(r => r.id === route.id);
    if (idx !== -1) {
      routes.value[idx] = {
        ...data,
        stops: (data.stops || []).map(stop => ({
          ...stop,
          delivery_status: normalizeStopDeliveryStatus(stop.delivery_status),
        })),
      };
    }
    if (selectedRoute.value?.id === route.id) {
      selectedRoute.value = routes.value[idx] || selectedRoute.value;
    }
    useAlert(copy.messages.updated);
  } catch {
    useAlert(copy.messages.error);
  }
};

const formatDate = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatStartsAt = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

onMounted(() => {
  fetchRoutes();
  fetchGoogleMapsConfig();
});
</script>

<template>
  <div class="flex h-full flex-col bg-n-solid-2">
    <!-- Header -->
    <div
      class="flex shrink-0 items-center justify-between border-b border-n-weak bg-n-solid-2 px-6 py-5"
    >
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ copy.title }}
        </h1>
        <p class="mt-0.5 text-sm text-n-slate-11">
          {{ copy.subtitle }}
        </p>
      </div>
      <div class="flex flex-wrap items-center justify-end gap-2">
        <button
          class="inline-flex items-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          @click="openGoogleMapsConfig"
        >
          <span class="i-lucide-key-round h-4 w-4" />
          {{ copy.actions.googleMapsSettings }}
        </button>
        <button
          class="inline-flex items-center gap-2 px-4 py-2 bg-woot-500 hover:bg-woot-600 text-white text-sm font-medium rounded-lg transition-colors"
          @click="openNewRoute"
        >
          <span class="i-lucide-plus w-4 h-4" />
          {{ copy.actions.newRoute }}
        </button>
      </div>
    </div>

    <!-- Search bar -->
    <div class="shrink-0 border-b border-n-weak bg-n-solid-2 px-6 py-3">
      <div class="relative w-full max-w-sm">
        <span
          class="pointer-events-none absolute inset-y-0 left-4 flex w-4 items-center justify-center text-slate-400"
        >
          <span class="i-lucide-search h-4 w-4" />
        </span>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="copy.fields.search"
          class="w-full !pl-12 rounded-lg border border-n-weak bg-n-solid-1 py-2 pr-4 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-300"
        />
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto bg-n-solid-2 p-6">
      <div
        v-if="isLoading"
        class="flex items-center justify-center h-40 text-slate-400 gap-2"
      >
        <span class="i-lucide-loader-2 w-5 h-5 animate-spin" />
        <span class="text-sm">{{ copy.labels.loading }}</span>
      </div>

      <div
        v-else-if="!filteredRoutes.length"
        class="flex flex-col items-center justify-center h-60 text-slate-400"
      >
        <span class="i-lucide-map-pinned w-12 h-12 mb-3 opacity-30" />
        <p class="font-medium text-slate-500 dark:text-slate-400">
          {{ copy.labels.noRoutes }}
        </p>
        <p class="text-sm mt-1">{{ copy.labels.noRouteDesc }}</p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div
          v-for="route in filteredRoutes"
          :key="route.id"
          class="flex cursor-pointer flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 transition-shadow hover:shadow-md"
          @click="openDetailRoute(route)"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1 min-w-0">
              <h3
                class="font-semibold text-slate-800 dark:text-slate-100 truncate"
              >
                {{ route.name }}
              </h3>
              <p class="text-xs text-slate-400 mt-0.5">
                {{ formatDate(route.created_at) }}
              </p>
            </div>
            <span
              :class="[
                'text-xs font-medium px-2 py-0.5 rounded-full',
                statusColor(route.status),
              ]"
            >
              {{ copy.status[route.status] }}
            </span>
          </div>

          <div
            class="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300"
          >
            <span
              class="i-lucide-map-pin w-4 h-4 text-slate-400 shrink-0 mt-0.5"
            />
            <span class="truncate">{{ route.origin_address }}</span>
          </div>

          <div
            class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400"
          >
            <span class="i-lucide-milestone w-4 h-4" />
            <span
              >{{ (route.stops || []).length }} {{ copy.labels.stops }}</span
            >
          </div>

          <div
            v-if="route.google_maps_url"
            class="flex gap-2 flex-wrap"
            @click.stop
          >
            <a
              :href="route.google_maps_url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium rounded-lg transition-colors"
            >
              <span class="i-lucide-map w-3.5 h-3.5" />
              Google Maps
            </a>
            <a
              v-if="route.waze_url"
              :href="route.waze_url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-medium rounded-lg transition-colors"
            >
              <span class="i-lucide-navigation w-3.5 h-3.5" />
              Waze
            </a>
          </div>

          <div
            class="flex items-center gap-2 pt-1 border-t border-slate-50 dark:border-slate-700"
            @click.stop
          >
            <button
              class="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
              @click="openEditRoute(route)"
            >
              <span class="i-lucide-pencil w-3.5 h-3.5" />
              {{ copy.actions.edit }}
            </button>
            <button
              :disabled="isGenerating"
              class="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
              @click="generateRoute(route)"
            >
              <span class="i-lucide-route w-3.5 h-3.5" />
              {{ copy.actions.generate }}
            </button>
            <button
              class="flex items-center justify-center p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
              @click="deleteRoute(route)"
            >
              <span class="i-lucide-trash-2 w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Google Maps Config Dialog -->
    <Dialog ref="googleMapsConfigDialogRef" width="xl" overflow-y-auto>
      <template #title>{{ copy.labels.googleMapsConfig }}</template>

      <div class="flex w-full flex-col gap-4 p-0 sm:p-4">
        <div
          class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2 text-sm font-medium">
              <span
                :class="[
                  'h-2.5 w-2.5 rounded-full',
                  googleMapsConfig.api_key_configured
                    ? 'bg-green-500'
                    : 'bg-amber-500',
                ]"
              />
              <span class="text-slate-800 dark:text-slate-100">
                {{
                  googleMapsConfig.api_key_configured
                    ? copy.labels.googleMapsConfigured
                    : copy.labels.googleMapsNotConfigured
                }}
              </span>
            </div>
            <span
              v-if="googleMapsConfig.masked_api_key"
              class="font-mono text-xs text-slate-500 dark:text-slate-400"
            >
              {{ googleMapsConfig.masked_api_key }}
            </span>
          </div>
          <p class="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
            {{ copy.labels.googleMapsHint }}
          </p>
        </div>

        <div
          class="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/40"
        >
          <p
            class="mb-2 text-sm font-medium text-slate-800 dark:text-slate-100"
          >
            {{ copy.labels.googleMapsTutorialTitle }}
          </p>
          <ol
            class="flex list-decimal flex-col gap-1.5 pl-4 text-xs leading-5 text-slate-600 dark:text-slate-300"
          >
            <li v-for="step in googleMapsTutorialSteps" :key="step">
              {{ step }}
            </li>
          </ol>

          <div class="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <p
                class="mb-1 text-xs font-medium text-slate-700 dark:text-slate-200"
              >
                {{ copy.labels.googleMapsProductsTitle }}
              </p>
              <ul
                class="flex list-disc flex-col gap-1 pl-4 text-xs leading-5 text-slate-500 dark:text-slate-400"
              >
                <li v-for="product in googleMapsProducts" :key="product">
                  {{ product }}
                </li>
              </ul>
            </div>
            <div>
              <p
                class="mb-1 text-xs font-medium text-slate-700 dark:text-slate-200"
              >
                {{ copy.labels.googleMapsSecurityTitle }}
              </p>
              <ul
                class="flex list-disc flex-col gap-1 pl-4 text-xs leading-5 text-slate-500 dark:text-slate-400"
              >
                <li v-for="tip in googleMapsSecurityTips" :key="tip">
                  {{ tip }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-slate-600 dark:text-slate-400">
            {{ copy.fields.googleMapsApiKey }}
          </label>
          <input
            v-model="googleMapsForm.api_key"
            type="password"
            autocomplete="off"
            placeholder="AIza..."
            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-woot-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
          />
        </div>

        <label
          v-if="googleMapsConfig.api_key_configured"
          class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
        >
          <input
            v-model="googleMapsForm.clear_api_key"
            type="checkbox"
            class="h-4 w-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
          />
          {{ copy.actions.removeKey }}
        </label>
      </div>

      <template #footer>
        <div class="flex flex-wrap justify-end gap-3 px-0 pb-0 sm:px-4 sm:pb-4">
          <button
            class="rounded-lg px-4 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
            @click="googleMapsConfigDialogRef?.close()"
          >
            {{ copy.actions.cancel }}
          </button>
          <button
            :disabled="isSavingGoogleMapsConfig || isLoadingGoogleMapsConfig"
            class="inline-flex items-center gap-2 rounded-lg bg-woot-500 px-4 py-2 text-sm text-white transition-colors hover:bg-woot-600 disabled:opacity-50"
            @click="saveGoogleMapsConfig"
          >
            <span
              v-if="isSavingGoogleMapsConfig"
              class="i-lucide-loader-2 h-4 w-4 animate-spin"
            />
            {{ copy.actions.save }}
          </button>
        </div>
      </template>
    </Dialog>

    <!-- Create / Edit Dialog -->
    <Dialog ref="formDialogRef" width="3xl" overflow-y-auto>
      <template #title>
        {{ editingRoute ? copy.actions.edit : copy.actions.newRoute }}
      </template>

      <div class="flex w-full flex-col gap-4 p-0 sm:p-4">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="flex min-w-0 flex-col gap-1">
            <label
              class="text-xs font-medium text-slate-600 dark:text-slate-400"
              >{{ copy.fields.name }} *</label
            >
            <input
              v-model="form.name"
              type="text"
              :placeholder="copy.fields.name"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300"
            />
          </div>
          <div class="flex min-w-0 flex-col gap-1">
            <label
              class="text-xs font-medium text-slate-600 dark:text-slate-400"
              >{{ copy.fields.status }}</label
            >
            <select
              v-model="form.status"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300"
            >
              <option value="draft">{{ copy.status.draft }}</option>
              <option value="active">{{ copy.status.active }}</option>
              <option value="completed">{{ copy.status.completed }}</option>
            </select>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-slate-600 dark:text-slate-400"
            >{{ copy.fields.originAddress }} *</label
          >
          <input
            v-model="form.origin_address"
            type="text"
            :placeholder="copy.fields.originAddress"
            class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300"
          />

          <div class="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              :disabled="isLocatingOrigin"
              class="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
              @click="useCurrentOriginLocation"
            >
              <span
                v-if="isLocatingOrigin"
                class="i-lucide-loader-2 h-3.5 w-3.5 animate-spin"
              />

              <span v-else class="i-lucide-map-pin h-3.5 w-3.5" />

              {{ copy.actions.currentLocation }}
            </button>

            <button
              type="button"
              :disabled="isSearchingOrigin || !form.origin_address.trim()"
              class="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-700"
              @click="searchOriginAddress"
            >
              <span
                v-if="isSearchingOrigin"
                class="i-lucide-loader-2 h-3.5 w-3.5 animate-spin"
              />

              <span v-else class="i-lucide-search h-3.5 w-3.5" />

              {{ copy.actions.lookupAddress }}
            </button>
          </div>
        </div>

        <div>
          <div
            class="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300"
              >Paradas de entrega</span
            >
            <div class="flex flex-wrap gap-2">
              <button
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-700 rounded-lg border border-blue-200 dark:border-slate-600 transition-colors"
                @click="openSourceDialog"
              >
                <span class="i-lucide-database w-3.5 h-3.5" />
                {{ copy.actions.addFromSource }}
              </button>
              <button
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 transition-colors"
                @click="addManualStop"
              >
                <span class="i-lucide-plus w-3.5 h-3.5" />
                {{ copy.actions.addStop }}
              </button>
            </div>
          </div>

          <div
            v-if="!form.stops.length"
            class="rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-400 dark:bg-slate-700/50"
          >
            Nenhuma parada adicionada. Adicione da agenda, contratos ou
            manualmente.
          </div>

          <div class="flex flex-col gap-2 max-h-72 overflow-y-auto">
            <div
              v-for="(stop, idx) in form.stops"
              :key="stop.id"
              class="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 flex flex-col gap-2 border border-slate-100 dark:border-slate-600"
            >
              <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-1.5">
                  <span
                    class="w-5 h-5 flex items-center justify-center rounded-full bg-woot-100 text-woot-700 text-xs font-bold"
                  >
                    {{ idx + 1 }}
                  </span>
                  <span
                    :class="[
                      'text-xs font-medium px-1.5 py-0.5 rounded',
                      sourceTypeColor(stop.source_type),
                    ]"
                  >
                    {{ copy.sourceType[stop.source_type] || stop.source_type }}
                  </span>
                </div>
                <div class="flex items-center gap-1">
                  <button
                    :disabled="idx === 0"
                    class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-30 rounded"
                    @click="moveStop(idx, -1)"
                  >
                    <span class="i-lucide-chevron-up w-3.5 h-3.5" />
                  </button>
                  <button
                    :disabled="idx === form.stops.length - 1"
                    class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-30 rounded"
                    @click="moveStop(idx, 1)"
                  >
                    <span class="i-lucide-chevron-down w-3.5 h-3.5" />
                  </button>
                  <button
                    class="p-1 text-slate-400 hover:text-red-500 rounded"
                    @click="removeStop(idx)"
                  >
                    <span class="i-lucide-x w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div class="flex flex-col gap-0.5">
                  <label class="text-xs text-slate-500">{{
                    copy.fields.stopName
                  }}</label>
                  <input
                    v-model="stop.name"
                    type="text"
                    class="w-full px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-300"
                  />
                </div>
                <div class="flex flex-col gap-0.5">
                  <label class="text-xs text-slate-500">{{
                    copy.fields.stopPhone
                  }}</label>
                  <input
                    v-model="stop.phone"
                    type="text"
                    class="w-full px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-300"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-0.5">
                <label class="text-xs text-slate-500"
                  >{{ copy.fields.stopAddress }} *</label
                >
                <input
                  v-model="stop.address"
                  type="text"
                  :placeholder="copy.labels.addressRequired"
                  class="w-full px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-300"
                />
              </div>

              <div class="flex flex-col gap-0.5">
                <label class="text-xs text-slate-500">{{
                  copy.fields.stopNotes
                }}</label>
                <input
                  v-model="stop.notes"
                  type="text"
                  class="w-full px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-300"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label
            class="text-xs font-medium text-slate-600 dark:text-slate-400"
            >{{ copy.fields.notes }}</label
          >
          <textarea
            v-model="form.notes"
            rows="2"
            class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300 resize-none"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex flex-wrap justify-end gap-3 px-0 pb-0 sm:px-4 sm:pb-4">
          <button
            class="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-slate-300 rounded-lg transition-colors"
            @click="formDialogRef?.close()"
          >
            {{ copy.actions.cancel }}
          </button>
          <button
            :disabled="isSaving"
            class="px-4 py-2 text-sm bg-woot-500 hover:bg-woot-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            @click="saveRoute"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 w-4 h-4 animate-spin"
            />
            {{ copy.actions.save }}
          </button>
        </div>
      </template>
    </Dialog>

    <!-- Source Selection Dialog -->
    <Dialog ref="sourceDialogRef" width="2xl" overflow-y-auto>
      <template #title>{{ copy.labels.selectSources }}</template>
      <div class="w-full p-0 sm:p-4">
        <div
          class="mb-4 flex overflow-x-auto border-b border-slate-200 dark:border-slate-700"
        >
          <button
            :class="[
              'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
              selectedSourceTab === 'contracts'
                ? 'text-woot-600 border-woot-500'
                : 'text-slate-500 border-transparent hover:text-slate-700',
            ]"
            @click="selectedSourceTab = 'contracts'"
          >
            {{ copy.labels.contracts }}
          </button>
          <button
            :class="[
              'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
              selectedSourceTab === 'events'
                ? 'text-woot-600 border-woot-500'
                : 'text-slate-500 border-transparent hover:text-slate-700',
            ]"
            @click="selectedSourceTab = 'events'"
          >
            {{ copy.labels.calendarEvents }}
          </button>
        </div>

        <div
          v-if="isLoadingSources"
          class="flex items-center justify-center h-32 text-slate-400 gap-2"
        >
          <span class="i-lucide-loader-2 w-5 h-5 animate-spin" />
          <span class="text-sm">{{ copy.labels.loading }}</span>
        </div>

        <div
          v-else-if="selectedSourceTab === 'contracts'"
          class="flex flex-col gap-2 max-h-80 overflow-y-auto"
        >
          <div
            v-if="!sources.contracts.length"
            class="text-sm text-slate-400 text-center py-8"
          >
            {{ copy.labels.noContracts }}
          </div>
          <div
            v-for="contract in sources.contracts"
            :key="contract.id"
            :class="[
              'flex flex-col gap-3 p-3 rounded-lg border transition-colors sm:flex-row sm:items-center sm:justify-between',
              isSourceAdded(contract)
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                : 'bg-slate-50 dark:bg-slate-700/50 border-slate-100 dark:border-slate-600 hover:border-woot-200',
            ]"
          >
            <div class="flex-1 min-w-0">
              <p
                class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate"
              >
                {{ contract.name }}
              </p>
              <p class="text-xs text-slate-400 truncate">
                {{ contract.phone }} · {{ contract.notes }}
              </p>
              <p
                v-if="contract.address"
                class="text-xs text-slate-500 mt-0.5 truncate"
              >
                <span class="i-lucide-map-pin w-3 h-3 inline" />
                {{ contract.address }}
              </p>
              <p v-else class="text-xs text-amber-500 mt-0.5">
                <span class="i-lucide-alert-circle w-3 h-3 inline" /> Sem
                endereço — preencha manualmente
              </p>
            </div>
            <button
              :disabled="isSourceAdded(contract)"
              :class="[
                'shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors sm:ml-3',
                isSourceAdded(contract)
                  ? 'bg-green-100 text-green-700 cursor-default'
                  : 'bg-woot-500 hover:bg-woot-600 text-white',
              ]"
              @click="addSourceToStops(contract)"
            >
              {{ isSourceAdded(contract) ? 'Adicionado' : 'Adicionar' }}
            </button>
          </div>
        </div>

        <div v-else class="flex flex-col gap-2 max-h-80 overflow-y-auto">
          <div
            v-if="!sources.calendar_events.length"
            class="text-sm text-slate-400 text-center py-8"
          >
            {{ copy.labels.noEvents }}
          </div>
          <div
            v-for="event in sources.calendar_events"
            :key="event.id"
            :class="[
              'flex flex-col gap-3 p-3 rounded-lg border transition-colors sm:flex-row sm:items-center sm:justify-between',
              isSourceAdded(event)
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                : 'bg-slate-50 dark:bg-slate-700/50 border-slate-100 dark:border-slate-600 hover:border-woot-200',
            ]"
          >
            <div class="flex-1 min-w-0">
              <p
                class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate"
              >
                {{ event.name }}
              </p>
              <p class="text-xs text-slate-400">
                {{ formatStartsAt(event.starts_at) }}
              </p>
              <p
                v-if="event.address"
                class="text-xs text-slate-500 mt-0.5 truncate"
              >
                <span class="i-lucide-map-pin w-3 h-3 inline" />
                {{ event.address }}
              </p>
              <p v-else class="text-xs text-amber-500 mt-0.5">
                <span class="i-lucide-alert-circle w-3 h-3 inline" /> Sem local
                — preencha manualmente
              </p>
            </div>
            <button
              :disabled="isSourceAdded(event)"
              :class="[
                'shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors sm:ml-3',
                isSourceAdded(event)
                  ? 'bg-green-100 text-green-700 cursor-default'
                  : 'bg-woot-500 hover:bg-woot-600 text-white',
              ]"
              @click="addSourceToStops(event)"
            >
              {{ isSourceAdded(event) ? 'Adicionado' : 'Adicionar' }}
            </button>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end px-0 pb-0 sm:px-4 sm:pb-4">
          <button
            class="px-4 py-2 text-sm bg-woot-500 hover:bg-woot-600 text-white rounded-lg transition-colors"
            @click="sourceDialogRef?.close()"
          >
            {{ copy.actions.close }}
          </button>
        </div>
      </template>
    </Dialog>

    <!-- Route Detail Dialog -->
    <Dialog ref="detailDialogRef" width="2xl" overflow-y-auto>
      <template #title>
        <div class="flex items-center gap-2">
          <span>{{ selectedRoute?.name }}</span>
          <span
            v-if="selectedRoute"
            :class="[
              'text-xs font-medium px-2 py-0.5 rounded-full',
              statusColor(selectedRoute.status),
            ]"
            >{{ copy.status[selectedRoute.status] }}</span
          >
        </div>
      </template>
      <div v-if="selectedRoute" class="flex w-full flex-col gap-4 p-0 sm:p-4">
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-start gap-3">
            <div
              class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100"
            >
              <span class="i-lucide-flag h-4 w-4 text-green-700" />
            </div>
            <div>
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ copy.labels.origin }}
              </p>
              <p class="mt-0.5 text-sm text-n-slate-12">
                {{ selectedRoute.origin_address }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="selectedRoute.stops?.length"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <div class="mb-3 flex items-center justify-between">
            <p class="text-sm font-semibold text-n-slate-12">Paradas</p>
            <span class="text-xs text-n-slate-10">
              {{ selectedRoute.stops.length }} {{ copy.labels.stops }}
            </span>
          </div>
          <div class="flex max-h-72 flex-col gap-2 overflow-y-auto pr-1">
            <div
              v-for="(stop, idx) in selectedRoute.stops"
              :key="stop.id || idx"
              :class="[
                'flex items-start gap-3 rounded-lg p-3 transition-colors',
                stopCardColor(stop.delivery_status),
              ]"
            >
              <div
                class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-woot-100"
              >
                <span class="text-xs font-bold text-woot-700">{{
                  idx + 1
                }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <p class="truncate text-sm font-medium text-n-slate-12">
                    {{ stop.name || '—' }}
                  </p>
                  <span
                    :class="[
                      'rounded px-1.5 py-0.5 text-xs',
                      sourceTypeColor(stop.source_type),
                    ]"
                    >{{
                      copy.sourceType[stop.source_type] || stop.source_type
                    }}</span
                  >
                  <span
                    :class="[
                      'rounded border px-1.5 py-0.5 text-xs font-medium',
                      stopStatusColor(stop.delivery_status),
                    ]"
                    >{{
                      copy.stopStatus[
                        normalizeStopDeliveryStatus(stop.delivery_status)
                      ]
                    }}</span
                  >
                </div>
                <p
                  v-if="stop.address"
                  class="mt-1 truncate text-xs text-n-slate-11"
                >
                  <span class="i-lucide-map-pin w-3 h-3 inline mr-0.5" />{{
                    stop.address
                  }}
                </p>
                <p v-if="stop.phone" class="mt-0.5 text-xs text-n-slate-10">
                  <span class="i-lucide-phone w-3 h-3 inline mr-0.5" />{{
                    stop.phone
                  }}
                </p>
                <p
                  v-if="stop.notes"
                  class="mt-0.5 truncate text-xs text-n-slate-10"
                >
                  {{ stop.notes }}
                </p>
                <div class="mt-2 flex flex-wrap gap-2">
                  <button
                    :class="[
                      'inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
                      isStopStatus(stop, 'delivered')
                        ? 'border-green-200 bg-green-100 text-green-700'
                        : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-2',
                    ]"
                    @click="
                      updateStopDeliveryStatus(selectedRoute, idx, 'delivered')
                    "
                  >
                    <span class="i-lucide-check w-3.5 h-3.5" />
                    {{ copy.actions.markDelivered }}
                  </button>
                  <button
                    :class="[
                      'inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
                      isStopStatus(stop, 'not_delivered')
                        ? 'border-rose-200 bg-rose-100 text-rose-700'
                        : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-2',
                    ]"
                    @click="
                      updateStopDeliveryStatus(
                        selectedRoute,
                        idx,
                        'not_delivered'
                      )
                    "
                  >
                    <span class="i-lucide-x w-3.5 h-3.5" />
                    {{ copy.actions.markNotDelivered }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="rounded-xl border border-dashed border-n-weak bg-n-alpha-2 py-6 text-center text-sm text-n-slate-10"
        >
          Nenhuma parada cadastrada.
        </div>

        <div
          v-if="selectedRoute.google_maps_url"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <p
            class="mb-3 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-navigation w-4 h-4" />
            {{ copy.labels.navigationLinks }}
          </p>
          <div class="flex flex-wrap gap-2">
            <a
              :href="selectedRoute.google_maps_url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 rounded-lg bg-woot-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-woot-600"
            >
              <span class="i-lucide-map h-3.5 w-3.5" />
              {{ copy.actions.openGoogleMaps }}
            </a>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2"
              @click="copyMapLink(selectedRoute)"
            >
              <span class="i-lucide-link h-3.5 w-3.5" />
              {{ copy.actions.copyGoogleMapsLink }}
            </button>
            <a
              v-if="selectedRoute.waze_url"
              :href="selectedRoute.waze_url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2"
            >
              <span class="i-lucide-navigation-2 h-3.5 w-3.5" />
              {{ copy.actions.openWaze }}
            </a>
          </div>
        </div>
        <div
          v-else
          class="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2"
        >
          <span class="i-lucide-alert-circle w-4 h-4 shrink-0" />
          Rota ainda não gerada. Clique em "Gerar rota otimizada" para criar os
          links de navegação.
        </div>

        <div
          v-if="selectedRoute.notes"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 text-sm text-n-slate-11"
        >
          {{ selectedRoute.notes }}
        </div>
      </div>
      <template #footer>
        <div
          class="flex flex-col gap-3 px-0 pb-0 sm:flex-row sm:items-center sm:justify-between sm:px-4 sm:pb-4"
        >
          <div class="flex flex-wrap gap-2">
            <button
              v-if="selectedRoute?.status !== 'completed'"
              class="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-100 px-2.5 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-200"
              @click="completeRoute(selectedRoute)"
            >
              <span class="i-lucide-check-circle h-3.5 w-3.5" />
              {{ copy.actions.complete }}
            </button>
            <button
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-xs font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2"
              @click="
                () => {
                  detailDialogRef?.close();
                  openEditRoute(selectedRoute);
                }
              "
            >
              <span class="i-lucide-pencil h-3.5 w-3.5" />
              {{ copy.actions.edit }}
            </button>
          </div>
          <button
            :disabled="isGenerating"
            class="inline-flex items-center gap-1.5 rounded-lg bg-woot-500 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-woot-600 disabled:opacity-50"
            @click="generateRoute(selectedRoute)"
          >
            <span
              v-if="isGenerating"
              class="i-lucide-loader-2 h-3.5 w-3.5 animate-spin"
            />
            <span v-else class="i-lucide-route h-3.5 w-3.5" />
            {{ copy.actions.generate }}
          </button>
        </div>
      </template>
    </Dialog>
  </div>
</template>
